import React from 'react';

import { Link, useLocation } from 'react-router-dom';
import {
  FaFacebookF,
  FaInstagram,
  FaTwitter,
  FaYoutube,
  FaEnvelope,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaArrowUp
} from 'react-icons/fa';

/* Promises the shop actually makes elsewhere in the app (free delivery at
   checkout, the refund flow, COD + Razorpay, the support-ticket system). */
const TRUST_POINTS = [
  { icon: '🚚', title: 'Free Delivery', text: 'On every order, no minimum' },
  { icon: '↩', title: 'Easy Returns', text: 'Request a refund after delivery' },
  { icon: '🔒', title: 'Secure Payments', text: 'UPI, cards, net banking & COD' },
  { icon: '🎫', title: 'Real Support', text: 'Raise a ticket, track the reply' }
];


function Footer() {
  const year = new Date().getFullYear();
  const location = useLocation();

  const scrollToTop = () =>
    window.scrollTo({
      top: 0,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth'
    });

  /*
    On the delivery-partner portal we don't want the customer shop/account
    links (they'd send a partner into the customer side of the app). Show a
    slim, partner-appropriate footer there instead.
  */
  const isDeliveryPortal = location.pathname.startsWith('/delivery');

  if (isDeliveryPortal) {
    return (
      <footer className="shopease-footer">
        <div className="shopease-footer-inner">
          <div className="shopease-footer-partner">
            <div className="shopease-footer-brand">
              🛵 <span>PoojaMart Partner</span>
            </div>
            <p className="shopease-footer-about mb-0">
              Delivering happiness, one order at a time.
            </p>

            <div className="shopease-partner-contacts">
              <a
                href="mailto:partners@shopease.com"
                className="shopease-footer-partner-mail"
              >
                <FaEnvelope /> partners@shopease.com
              </a>

              <a
                href="tel:+919876543210"
                className="shopease-footer-partner-mail"
              >
                <FaPhoneAlt /> +91 98765 43210
              </a>
            </div>
          </div>

          <div className="shopease-footer-bottom">
            <p className="mb-0">© {year} PoojaMart. All Rights Reserved.</p>
            <div className="shopease-footer-legal">
              <a href="#!">Partner Terms</a>
              <span>•</span>
              <a href="#!">Support</a>
            </div>
          </div>
        </div>

        <style>{footerStyles}</style>
      </footer>
    );
  }

  return (
    <footer className="shopease-footer">
      {/* Kolam patterns that draw themselves outward, hold, then fade */}
      <span className="sf-kolam sf-kolam-left" aria-hidden="true" />
      <span className="sf-kolam sf-kolam-right" aria-hidden="true" />

      <div className="shopease-footer-inner">
        {/* Trust strip */}
        <div className="shopease-trust">
          {TRUST_POINTS.map((point, i) => (
            <div className="shopease-trust-item" key={point.title}>
              {/* The blessing passes from one promise to the next */}
              <span
                className="shopease-trust-icon"
                style={{ animationDelay: `${i * 1.5}s` }}
              >
                {point.icon}
              </span>
              <div className="shopease-trust-text">
                <div className="shopease-trust-title">{point.title}</div>
                <small>{point.text}</small>
              </div>
            </div>
          ))}
        </div>

        <div className="shopease-footer-grid">
          {/* Brand */}
          <div className="shopease-footer-col shopease-footer-brand-col">
            <div className="shopease-footer-brand">
              🛍️ <span>PoojaMart</span>
            </div>

            <p className="shopease-footer-about">
              Your one-stop shop for the latest products at unbeatable prices.
              Quality you can trust, delivered to your door.
            </p>

            <div className="shopease-social">
              <a href="#!" aria-label="Facebook" className="shopease-social-btn">
                <FaFacebookF />
              </a>
              <a href="#!" aria-label="Instagram" className="shopease-social-btn">
                <FaInstagram />
              </a>
              <a href="#!" aria-label="Twitter" className="shopease-social-btn">
                <FaTwitter />
              </a>
              <a href="#!" aria-label="YouTube" className="shopease-social-btn">
                <FaYoutube />
              </a>
            </div>
          </div>

          {/* Shop */}
          <div className="shopease-footer-col">
            <h6 className="shopease-footer-title">Shop</h6>
            <ul className="shopease-footer-links">
              <li>
                <Link to="/products">All Products</Link>
              </li>
              <li>
                <Link to="/categories">Categories</Link>
              </li>
              <li>
                <Link to="/cart">Cart</Link>
              </li>
              <li>
                <Link to="/wishlist">Wishlist</Link>
              </li>
            </ul>
          </div>

          {/* Account */}
          <div className="shopease-footer-col">
            <h6 className="shopease-footer-title">Account</h6>
            <ul className="shopease-footer-links">
              <li>
                <Link to="/profile">My Profile</Link>
              </li>
              <li>
                <Link to="/orders">My Orders</Link>
              </li>
              <li>
                <Link to="/my-refunds">My Refunds</Link>
              </li>
              <li>
                <Link to="/my-tickets">My Tickets</Link>
              </li>
              <li>
                <Link to="/help">Help &amp; Support</Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="shopease-footer-col">
            <h6 className="shopease-footer-title">Get in Touch</h6>
            <ul className="shopease-footer-contact">
              <li>
                <span className="shopease-footer-ic">
                  <FaMapMarkerAlt />
                </span>
                <span>123 Market Street, Chennai, India</span>
              </li>
              <li>
                <span className="shopease-footer-ic">
                  <FaPhoneAlt />
                </span>
                <a href="tel:+919876543210">+91 98765 43210</a>
              </li>
              <li>
                <span className="shopease-footer-ic">
                  <FaEnvelope />
                </span>
                <a href="mailto:support@shopease.com">support@shopease.com</a>
              </li>
            </ul>

            <Link to="/delivery/login" className="shopease-partner-cta">
              🛵 Deliver with us
            </Link>
          </div>
        </div>

        {/* Copyright, accepted payments and legal all share one strip so the
            footer doesn't stack three separately-bordered bands. */}
        <div className="shopease-footer-bottom">
          <p className="mb-0">© {year} PoojaMart. All Rights Reserved.</p>

          <div className="shopease-pay-list" title="Accepted payment methods">
            {['UPI', 'Visa', 'Mastercard', 'RuPay', 'Net Banking', 'COD'].map(
              (method, i) => (
                <span
                  className="shopease-pay-chip"
                  key={method}
                  style={{ animationDelay: `${i * 0.35}s` }}
                >
                  {method}
                </span>
              )
            )}
          </div>

          <div className="shopease-footer-legal">
            <a href="#!">Privacy</a>
            <span>•</span>
            <a href="#!">Terms</a>

            <button
              type="button"
              className="shopease-top-btn"
              onClick={scrollToTop}
              aria-label="Back to top"
              title="Back to top"
            >
              <FaArrowUp />
            </button>
          </div>
        </div>
      </div>

      <style>{footerStyles}</style>
    </footer>
  );
}

const footerStyles = `
  .shopease-footer {
    position: relative;
    overflow: hidden;
    background: linear-gradient(90deg, #150a1f 0%, #460d16 32%, #7c2d12 68%, #b45309 100%);
    color: #e5e7eb;
    margin-top: auto;
  }
  /* Gold hairline capping the footer — the light runs along it */
  .shopease-footer::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    z-index: 3;
    background: linear-gradient(90deg, transparent, #fde68a 18%, #f59e0b 50%, #fde68a 82%, transparent);
    background-size: 200% 100%;
    animation: sfThread 5s linear infinite;
  }
  @keyframes sfThread {
    from { background-position: 200% 0; }
    to   { background-position: -200% 0; }
  }
  .shopease-footer-inner {
    position: relative;
    z-index: 2;
    max-width: 1200px;
    margin: 0 auto;
    padding: clamp(24px, 3.5vw, 34px) clamp(20px, 5vw, 48px) 14px;
  }

  /* ===== Kolam =====
     A rangoli drawn on the threshold: the pattern is revealed by growing a
     radial mask outward from its centre, held, then faded and redrawn. */
  .sf-kolam {
    position: absolute;
    z-index: 0;
    pointer-events: none;
    border-radius: 50%;
    background-image:
      /* petal spokes */
      repeating-conic-gradient(from 0deg, rgba(253,230,138,0.22) 0deg 1.6deg, transparent 1.6deg 15deg),
      /* concentric rings of dots */
      repeating-radial-gradient(circle, rgba(253,230,138,0.28) 0 1.6px, transparent 1.6px 24px);
    -webkit-mask-image: radial-gradient(circle, #000 58%, transparent 72%);
    mask-image: radial-gradient(circle, #000 58%, transparent 72%);
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    -webkit-mask-position: center;
    mask-position: center;
    -webkit-mask-size: 0% 0%;
    mask-size: 0% 0%;
    animation: sfKolamDraw 11s ease-in-out infinite;
  }
  .sf-kolam-left {
    left: -70px;
    bottom: -80px;
    width: 260px;
    height: 260px;
  }
  .sf-kolam-right {
    right: -80px;
    top: -90px;
    width: 300px;
    height: 300px;
    animation-delay: -5.5s;
  }
  @keyframes sfKolamDraw {
    0%   { -webkit-mask-size: 0% 0%;     mask-size: 0% 0%;     opacity: 0; }
    10%  { opacity: 1; }
    50%  { -webkit-mask-size: 175% 175%; mask-size: 175% 175%; opacity: 1; }
    78%  { -webkit-mask-size: 175% 175%; mask-size: 175% 175%; opacity: 0.85; }
    100% { -webkit-mask-size: 175% 175%; mask-size: 175% 175%; opacity: 0; }
  }

  /* ===== Trust strip ===== */
  .shopease-trust {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px clamp(12px, 2vw, 20px);
    padding-bottom: 18px;
    margin-bottom: 22px;
    border-bottom: 1px solid rgba(255,255,255,0.12);
  }
  .shopease-trust-item {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }
  .shopease-trust-icon {
    width: 34px;
    height: 34px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    border-radius: 11px;
    background: rgba(255,255,255,0.10);
    border: 1px solid rgba(255,255,255,0.16);
    transition: transform 0.25s ease, background 0.25s ease;
    /* Each promise is lit in turn, 1.5s apart, on a 6s round */
    animation: sfBless 6s ease-in-out infinite;
  }
  @keyframes sfBless {
    0%, 16%, 100% {
      transform: translateY(0);
      background: rgba(255,255,255,0.10);
      box-shadow: none;
    }
    7% {
      transform: translateY(-5px);
      background: linear-gradient(135deg, #fde68a, #f59e0b);
      box-shadow: 0 8px 18px rgba(251, 191, 36, 0.55);
    }
  }
  /* Hovering takes over from the cycle */
  .shopease-trust-item:hover .shopease-trust-icon {
    animation: none;
    transform: translateY(-3px);
    background: linear-gradient(135deg, #fbbf24, #ea580c);
  }
  .shopease-trust-text { min-width: 0; }
  .shopease-trust-title {
    font-weight: 700;
    font-size: 0.84rem;
    color: #fff;
    line-height: 1.2;
  }
  .shopease-trust-text small {
    color: #cbd5e1;
    font-size: 0.71rem;
    line-height: 1.3;
    display: block;
  }

  .shopease-footer-grid {
    display: grid;
    grid-template-columns: 1.8fr 1fr 1fr 1.4fr;
    gap: 20px clamp(24px, 4vw, 48px);
    /* keeps every column's content locked to the same top edge */
    align-items: start;
  }

  .shopease-footer-brand {
    font-size: 1.35rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 8px;
    /* matches .shopease-footer-title so all four columns' bodies line up */
    min-height: 26px;
    margin-bottom: 12px;
  }
  /* Light keeps flowing through the wordmark */
  .shopease-footer-brand span {
    background: linear-gradient(90deg, #fbbf24, #fff7ed 45%, #ea580c 70%, #fbbf24);
    background-size: 220% auto;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: sfBrandFlow 7s linear infinite;
  }
  @keyframes sfBrandFlow {
    to { background-position: 220% center; }
  }
  .shopease-footer-about {
    font-size: 0.85rem;
    line-height: 1.55;
    color: #cbd5e1;
    max-width: 300px;
    margin-bottom: 0;
  }

  .shopease-social {
    display: flex;
    gap: 9px;
    margin-top: 14px;
  }
  .shopease-social-btn {
    width: 34px;
    height: 34px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: #fff;
    background: rgba(255,255,255,0.1);
    transition: transform 0.2s ease, background 0.2s ease;
  }
  .shopease-social-btn:hover {
    transform: translateY(-3px);
    background: linear-gradient(135deg, #fbbf24, #ea580c);
    color: #111827;
  }

  .shopease-footer-title {
    position: relative;
    font-size: 0.92rem;
    font-weight: 700;
    color: #fff;
    /* same box as .shopease-footer-brand so the lists start on one line */
    min-height: 26px;
    margin-bottom: 12px;
    padding-bottom: 7px;
    letter-spacing: 0.4px;
  }
  /* Small gold accent under each heading, drawing itself out and back */
  .shopease-footer-title::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 26px;
    height: 2px;
    border-radius: 2px;
    background: linear-gradient(90deg, #fbbf24, #ea580c);
    animation: sfAccent 4.5s ease-in-out infinite;
  }
  @keyframes sfAccent {
    0%, 100% { width: 26px; box-shadow: none; }
    50%      { width: 46px; box-shadow: 0 0 10px rgba(251, 191, 36, 0.6); }
  }

  .shopease-footer-links,
  .shopease-footer-contact {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .shopease-footer-links a {
    display: inline-flex;
    align-items: center;
    color: #cbd5e1;
    text-decoration: none;
    font-size: 0.86rem;
    /* transform, not padding — padding reflowed the text on hover */
    transition: color 0.2s ease, transform 0.2s ease;
  }
  .shopease-footer-links a::before {
    content: '›';
    display: inline-block;
    width: 0;
    opacity: 0;
    color: #fbbf24;
    font-weight: 700;
    transition: width 0.2s ease, opacity 0.2s ease;
  }
  .shopease-footer-links a:hover {
    color: #fbbf24;
    transform: translateX(4px);
  }
  .shopease-footer-links a:hover::before {
    width: 12px;
    opacity: 1;
  }

  .shopease-footer-contact li {
    display: flex;
    /* centred against the icon box instead of a magic-number nudge */
    align-items: center;
    gap: 10px;
    font-size: 0.86rem;
    line-height: 1.4;
    color: #cbd5e1;
  }
  .shopease-footer-contact a {
    color: #cbd5e1;
    text-decoration: none;
    transition: color 0.2s ease;
  }
  .shopease-footer-contact a:hover {
    color: #fbbf24;
  }
  .shopease-footer-ic {
    width: 27px;
    height: 27px;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 9px;
    font-size: 0.82rem;
    color: #fbbf24;
    background: rgba(255,255,255,0.08);
    border: 1px solid rgba(255,255,255,0.12);
  }

  /* Partner call-to-action */
  .shopease-partner-cta {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 14px;
    padding: 7px 14px;
    border-radius: 30px;
    font-size: 0.82rem;
    font-weight: 700;
    text-decoration: none;
    color: #fff7ed;
    background: rgba(255,255,255,0.10);
    border: 1px solid rgba(255,255,255,0.22);
    transition: transform 0.2s ease, background 0.2s ease, color 0.2s ease;
  }
  .shopease-partner-cta:hover {
    transform: translateY(-2px);
    background: linear-gradient(135deg, #fbbf24, #ea580c);
    color: #431407;
  }

  /* ===== Payment methods ===== */
  .shopease-pay-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .shopease-pay-chip {
    position: relative;
    overflow: hidden;
    font-size: 0.68rem;
    font-weight: 700;
    color: #e5e7eb;
    background: rgba(255,255,255,0.08);
    border: 1px solid rgba(255,255,255,0.16);
    border-radius: 7px;
    padding: 4px 9px;
    white-space: nowrap;
  }
  /* A gold pass runs along the row of chips, one after another */
  .shopease-pay-chip::after {
    content: '';
    position: absolute;
    top: 0; left: -130%;
    width: 65%; height: 100%;
    background: linear-gradient(100deg, transparent, rgba(253, 230, 138, 0.55), transparent);
    transform: skewX(-18deg);
    pointer-events: none;
    animation: sfChipShine 5s ease-in-out infinite;
  }
  @keyframes sfChipShine {
    0%, 68%   { left: -130%; }
    100%      { left: 170%; }
  }

  /* Back to top */
  .shopease-top-btn {
    width: 30px;
    height: 30px;
    margin-left: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(255,255,255,0.22);
    border-radius: 50%;
    color: #fbbf24;
    background: rgba(255,255,255,0.08);
    font-size: 0.8rem;
    cursor: pointer;
    transition: transform 0.2s ease, background 0.2s ease, color 0.2s ease;
  }
  .shopease-top-btn:hover {
    transform: translateY(-3px);
    background: linear-gradient(135deg, #fbbf24, #ea580c);
    color: #431407;
  }
  /* The arrow lifts away every few seconds, hinting at what it does */
  .shopease-top-btn svg {
    animation: sfArrowHint 3.2s ease-in-out infinite;
  }
  @keyframes sfArrowHint {
    0%, 62%, 100% { transform: translateY(0);    opacity: 1; }
    78%           { transform: translateY(-7px); opacity: 0; }
    79%           { transform: translateY(7px);  opacity: 0; }
    92%           { transform: translateY(0);    opacity: 1; }
  }

  /* Slim partner-portal footer */
  .shopease-footer-partner {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 8px;
    padding-bottom: 8px;
  }
  .shopease-partner-contacts {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px;
    margin-top: 6px;
  }
  .shopease-footer-partner-mail {
    color: #fbbf24;
    text-decoration: none;
    font-weight: 600;
    font-size: 0.85rem;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 7px 15px;
    border-radius: 30px;
    background: rgba(255,255,255,0.08);
    border: 1px solid rgba(255,255,255,0.18);
    transition: transform 0.2s ease, background 0.2s ease, color 0.2s ease;
  }
  .shopease-footer-partner-mail:hover {
    transform: translateY(-2px);
    background: linear-gradient(135deg, #fbbf24, #ea580c);
    color: #431407;
  }

  .shopease-footer-bottom {
    margin-top: 22px;
    padding-top: 14px;
    border-top: 1px solid rgba(255,255,255,0.12);
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px 18px;
    font-size: 0.8rem;
    color: #94a3b8;
  }
  .shopease-footer-legal {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .shopease-footer-legal a {
    color: #94a3b8;
    text-decoration: none;
    transition: color 0.2s ease;
  }
  .shopease-footer-legal a:hover {
    color: #fbbf24;
  }

  @media (max-width: 991.98px) {
    .shopease-trust {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 18px;
    }
    .shopease-footer-grid {
      grid-template-columns: 1fr 1fr;
    }
    .shopease-footer-brand-col {
      grid-column: 1 / -1;
    }
    /* headings only need to align within a row, not across the wrap */
    .shopease-footer-brand { min-height: 0; }
  }

  @media (max-width: 575.98px) {
    .shopease-trust {
      grid-template-columns: 1fr;
      gap: 14px;
    }
    .shopease-footer-grid {
      grid-template-columns: 1fr;
      text-align: center;
    }
    .shopease-footer-title {
      min-height: 0;
    }
    /* the accent bar has to re-centre with its heading */
    .shopease-footer-title::after {
      left: 50%;
      transform: translateX(-50%);
    }
    .shopease-footer-about {
      max-width: 100%;
    }
    .shopease-social,
    .shopease-footer-links a,
    .shopease-footer-contact li,
    .shopease-pay-list {
      justify-content: center;
    }
    .shopease-footer-col {
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    .shopease-footer-bottom {
      justify-content: center;
      text-align: center;
    }
    .shopease-footer-legal {
      justify-content: center;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .shopease-trust-icon, .shopease-social-btn, .shopease-footer-links a,
    .shopease-footer-links a::before, .shopease-partner-cta,
    .shopease-top-btn {
      transition: none;
    }
    .shopease-footer::before, .sf-kolam,
    .shopease-trust-icon, .shopease-footer-brand span,
    .shopease-footer-title::after, .shopease-pay-chip::after,
    .shopease-top-btn svg {
      animation: none;
    }
    /* Leave the kolam drawn rather than half-finished */
    .sf-kolam {
      -webkit-mask-size: 175% 175%;
      mask-size: 175% 175%;
      opacity: 0.5;
    }
  }
`;

export default Footer;
